/**
 * Step-completion rules for the model wizard.
 *
 * Extracted from CreateModel.tsx so the "can the user press Next?" decision is
 * unit-testable on its own. It was previously inline, untested, and it took a
 * production outage to notice it disagreed with the render path.
 *
 * ── The incident this file exists to prevent (2026-08-20) ────────────────────
 * Editing an existing model was stuck forever on General Property Assumptions:
 * Next, Save & Exit and every sidebar step were disabled with nothing on screen
 * to fix.
 *
 * Three things had to line up:
 *   1. `GET /api/model_types/<id>` serves soft-deleted fields (active=false);
 *      admin "delete field" only flips the flag.
 *   2. Every render path filters `field.active === true`, so a soft-deleted
 *      field is never drawn — but the completeness check iterated ALL fields.
 *   3. PR #98 made `GET /api/user_models_version/<id>` return value rows for
 *      ACTIVE fields only. Before that, a soft-deleted field still came back
 *      with a (usually empty) row, so `find()` returned something and the check
 *      passed by accident. After it, `find()` returned undefined — and
 *      `undefined` was the one input the old check treated as a hard failure.
 *
 * New models were unaffected because the create path seeds a value row from the
 * same payload it validates against, so the two lists agreed by construction.
 *
 * The two invariants below follow from that, and the tests enforce them:
 *   A. Validate exactly the fields that are rendered — `active !== false`.
 *   B. A missing value row means "not filled in yet", not "invalid". It must
 *      behave identically to a row whose value is null, which is what the API
 *      returns for an unset field.
 */

export interface ModelTypeField {
  id?: string;
  field_key?: string;
  field_title?: string;
  field_type?: string;
  required?: boolean;
  time_phased?: boolean;
  active?: boolean;
}

export interface FieldValueRow {
  field_id?: string;
  field_key?: string;
  value?: unknown;
  start_month?: unknown;
  end_month?: unknown;
}

/**
 * The fields the wizard actually draws. Soft-deleted fields are excluded here
 * for the same reason CreateModel/ModelTypeDetail exclude them when rendering:
 * the user has no way to give them a value.
 */
export function visibleFields<T extends ModelTypeField>(
  fields: T[] | undefined | null,
): T[] {
  // Generic so callers keep their own richer field type (the create flow reads
  // `default_value` off the result).
  return (fields || []).filter((f) => f && f.active !== false);
}

/**
 * Resolve the stored value row for a field.
 *
 * `field_id` first: a model type can carry two fields sharing one `field_key`
 * (one live, one soft-deleted — production's Development type does exactly this
 * for "Gross Buildable Square Feet"), and matching on the key alone picks an
 * arbitrary twin. `field_key` remains as a fallback for rows written before the
 * id was threaded through.
 */
export function findFieldValue(
  fieldValues: FieldValueRow[] | undefined | null,
  field: ModelTypeField,
): FieldValueRow | undefined {
  const rows = fieldValues || [];
  if (field?.id) {
    const byId = rows.find((fv) => fv && fv.field_id && fv.field_id === field.id);
    if (byId) return byId;
  }
  if (field?.field_key) {
    return rows.find((fv) => fv && fv.field_key === field.field_key);
  }
  return undefined;
}

/** A value the user has not supplied. `null` is what the API sends for unset. */
function isBlank(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Is this one field satisfied?
 *
 * Note the deliberately permissive treatment of a required field that holds
 * `null`/`undefined`: that is the behaviour the edit flow has always had (the
 * API returns null for unset fields and the old check let null through), so
 * tightening it here would newly lock users out of models they can edit today.
 * Only `''` — which is what the create flow seeds — blocks. Making `required`
 * uniformly strict is a deliberate follow-up, not part of this fix.
 */
export function isFieldComplete(
  field: ModelTypeField,
  fieldValue: FieldValueRow | undefined,
): boolean {
  // Never validate a field that is not rendered.
  if (!field || field.active === false) return true;

  // Invariant B: an absent row is an unset value, not an invalid one.
  const row: FieldValueRow = fieldValue ?? {};

  const isValueComplete = !field.required || row.value !== '';

  let isDateValid = true;
  if (field.field_type === 'date' && !isBlank(row.value)) {
    isDateValid = /^\d{2}\/\d{2}\/\d{4}$/.test(String(row.value));
  }

  const isTimePhasedComplete =
    !field.time_phased || (row.start_month !== undefined && row.end_month !== undefined);

  return isValueComplete && isTimePhasedComplete && isDateValid;
}

/**
 * Every rendered field in the section that is not satisfied. The wizard uses
 * this both to gate Next and — just as importantly — to tell the user which
 * field is holding them up. A disabled button with no explanation is what
 * turned this defect into a day of lost production time.
 */
export function incompleteFields<T extends ModelTypeField>(
  fields: T[] | undefined | null,
  fieldValues: FieldValueRow[] | undefined | null,
): T[] {
  return visibleFields(fields).filter(
    (field) => !isFieldComplete(field, findFieldValue(fieldValues, field)),
  );
}

/** True when every rendered field in the section is satisfied. */
export function isSectionComplete(
  fields: ModelTypeField[] | undefined | null,
  fieldValues: FieldValueRow[] | undefined | null,
): boolean {
  return incompleteFields(fields, fieldValues).length === 0;
}

/** Human-readable labels for the fields blocking the step. */
export function blockingFieldLabels(
  fields: ModelTypeField[] | undefined | null,
  fieldValues: FieldValueRow[] | undefined | null,
): string[] {
  return incompleteFields(fields, fieldValues).map(
    (f) => f.field_title || f.field_key || 'Untitled field',
  );
}
